import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { brandingSchema } from '@traceio-org/platform-contracts';
import { cx } from './cx.js';
/** Up to two initials from a display name, for compact marks. */
export function initialsOf(displayName) {
    const words = displayName.trim().split(/\s+/).filter(Boolean);
    const letters = words.slice(0, 2).map((word) => Array.from(word)[0] ?? '');
    return letters.join('').toUpperCase();
}
/**
 * Resolves a validated logo reference to an absolute URL. Only https URLs and platform asset paths pass
 * the branding schema; the resolved URL is checked again so a base URL mistake cannot produce a
 * javascript: or data: source.
 */
export function resolveLogoUrl(logoUrl, assetBaseUrl) {
    try {
        const base = assetBaseUrl ?? (typeof window === 'undefined' ? undefined : window.location.origin);
        const url = new URL(logoUrl, base);
        if (url.protocol === 'https:')
            return url.toString();
        // Plain http only for a relative asset path on an http platform origin (local development).
        if (url.protocol === 'http:' && logoUrl.startsWith('/brand-assets/'))
            return url.toString();
        return null;
    }
    catch {
        return null;
    }
}
/**
 * A customer organization's mark: its logo when the branding has a usable one, its name otherwise.
 * Branding is plain data (a name, a logo URL, an accent color); no customer HTML or script is rendered.
 */
export function OrganizationMark({ branding, surface = 'light', assetBaseUrl, variant = 'full', className, }) {
    const [failedSrc, setFailedSrc] = useState(null);
    const parsed = brandingSchema.safeParse(branding);
    if (!parsed.success)
        return null;
    const { displayName, logoUrl } = parsed.data;
    const src = logoUrl ? resolveLogoUrl(logoUrl, assetBaseUrl) : null;
    if (src && failedSrc !== src) {
        return (_jsx("span", { className: cx('trace-auth-orgmark', 'trace-auth-orgmark--logo', className), "data-surface": surface, children: _jsx("img", { src: src, alt: displayName, referrerPolicy: "no-referrer", decoding: "async", onError: () => setFailedSrc(src) }) }));
    }
    if (variant === 'compact') {
        return (_jsxs("span", { className: cx('trace-auth-orgmark', 'trace-auth-orgmark--initials', className), "data-surface": surface, children: [_jsx("span", { "aria-hidden": "true", children: initialsOf(displayName) }), _jsx("span", { className: "trace-auth-visually-hidden", children: displayName })] }));
    }
    return (_jsx("span", { className: cx('trace-auth-orgmark', 'trace-auth-orgmark--text', className), "data-surface": surface, children: displayName }));
}
//# sourceMappingURL=OrganizationMark.js.map